/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import Modal from './modal/modal';
import Typography from './typography/typography';
import Button from './button/button';
import ModalLoader from './loaders/modal-loader';
import { DynamicInput, IModalComponentProps } from './form-control';

interface IFormValues {
    brand_data: {
        name: string;
    };
}

const brandValidationSchema = Yup.object().shape({
    brand_data: Yup.object().shape({
        name: Yup.string().required('Name is required')
    })
});

export const BrandAddModal: React.FC<IModalComponentProps> = ({
    isOpen,
    setIsOpen,
    onSave,
    isLoading,
    modalProps,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, trigger } = useForm<any>({
        mode: 'all',
        resolver: yupResolver(brandValidationSchema),
        defaultValues: {
            brand_data: {
                name: '',
            }
        }
    });

    const handleModalClose = () => {
        reset({ brand_data: { name: '' } });
        setIsOpen();
    };

    const onSubmit = async (values: IFormValues) => {
        const isValid = await trigger();
        if (!isValid) return;
        await onSave(values?.brand_data as any);
        handleModalClose();
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleModalClose}
            maxWidth='sm'
            fullWidth
            {...modalProps}
        >
            <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {isLoading && <ModalLoader />}
                    <DialogTitle className='brandAddModal--DialogTitle'>
                        <Typography type='s3' color='inherit' weight='medium'>
                            {t("inventory.item.brandModal.title")}
                        </Typography>
                        <IconButton onClick={handleModalClose}>
                            <Close fontSize='small' />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className='brandAddModal--DialogContents'>
                        <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
                            <Grid item sm={12}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t("inventory.item.brandModal.brand_name_label")}
                                    name='name'
                                    fieldArrayName='brand_data'
                                    placeholder={t("inventory.item.brandModal.brand_name_placeholder")}
                                    required
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions className='brandAddModal--DialogAction'>
                        <span></span>
                        <Box display='flex' gap={1.5}>
                            <Button
                                className='brandAddModal--DialogAction--WhiteBtn'
                                onClick={handleModalClose}
                            >
                                {t("common.cancel")}
                            </Button>
                            <Button type='button' onClick={handleSubmit(onSubmit)}>{t("common.save")}</Button>
                        </Box>
                    </DialogActions>
                </form>
            </>
        </Modal>
    );
};
